import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';

/**
 * Le tableau de l'application. Remplace `material-table`.
 *
 * ⛔ POURQUOI IL A FALLU LE REMPLACER. `material-table@1.69.3` exigeait
 * `@mui/material ^4` et `react ^16 || ^17` : il bloquait à lui seul la
 * montée de MUI **et** de React. Puis, à la bascule vers Vite, il a bloqué
 * l'outil de build lui-même — son `@material-ui/pickers` réexporte un type
 * TypeScript comme une valeur, ce que webpack 4 tolérait et que Rollup refuse.
 * Il emportait aussi `date-fns` en version alpha de 2019, `jspdf`,
 * `react-beautiful-dnd` et `dompurify`, avec leurs vulnérabilités.
 *
 * 🔑 UN SEUL COMPOSANT pour les quatre écrans qui en avaient besoin. Ils
 * demandaient tous la même chose : des colonnes, une recherche, une
 * pagination, et parfois des actions. Quatre tableaux écrits séparément
 * auraient divergé au premier correctif.
 */

const useStyles = makeStyles()((theme) => ({
  barre: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  recherche: {
    minWidth: 220,
  },
  ligneCliquable: {
    cursor: 'pointer',
  },
  actions: {
    whiteSpace: 'nowrap',
    textAlign: 'right',
  },
}));

/**
 * La valeur d'un champ, y compris en chemin pointé.
 *
 * Les colonnes des prêts s'écrivent `book.title` et `customer.first_name` :
 * c'est la notation que `material-table` acceptait, et la changer aurait
 * demandé de réécrire les quatre écrans plutôt que de les porter.
 */
export function valeurDe(objet, chemin) {
  return chemin.split('.').reduce((courant, cle) => (courant == null ? courant : courant[cle]), objet);
}

/**
 * Rend une valeur lisible.
 *
 * ⚠️ Les dates sont formatées avec la locale du navigateur plutôt qu'avec
 * `moment` : le paquet n'était plus importé nulle part et `Intl` fait le
 * travail sans dépendance.
 */
function afficher(valeur, colonne) {
  if (valeur == null || valeur === '') {
    return '';
  }
  if (colonne.type === 'date') {
    const date = new Date(valeur);
    return Number.isNaN(date.getTime()) ? String(valeur) : date.toLocaleDateString();
  }
  return String(valeur);
}

// 🔴 Les valeurs par défaut sont dans la SIGNATURE, pas dans « defaultProps ».
// React 19 a SUPPRIMÉ « defaultProps » pour les composants fonction — pas
// déprécié, supprimé. Les valeurs ne sont donc plus appliquées du tout, et un
// tableau attendu arrive « undefined » : « Cannot read properties of undefined
// (reading 'length') », écran blanc, sur la seule page qui n'en passait pas.
export default function Tableau({
  colonnes,
  donnees = [],
  titre = null,
  actions = [],
  onRowClick = null,
  styleDeLigne = null,
  recherche = true,
  pagination = true,
  selection = false,
  onSelectionChange = null,
  taillePage = 20,
}) {
  const { classes } = useStyles();
  const { t } = useTranslation('common');
  const [terme, setTerme] = useState('');
  const [page, setPage] = useState(0);
  const [parPage, setParPage] = useState(taillePage);
  const [selectionnees, setSelectionnees] = useState([]);

  // La recherche porte sur TOUTES les colonnes affichées, comme avant : une
  // personne qui cherche « Doucet » ne sait pas dans quelle colonne c'est.
  const filtrees = useMemo(() => {
    if (!terme.trim()) {
      return donnees;
    }
    const cherche = terme.trim().toLowerCase();
    // ⚠️ Pas de « ?? » ici : l'ESLint du projet est encore en version 6,
    // héritée de Create React App, et sa syntaxe s'arrête avant. Sa mise à
    // jour est la dernière phase de la migration.
    return donnees.filter((ligne) => colonnes.some((colonne) => {
      const valeur = valeurDe(ligne, colonne.field);
      return String(valeur === null || valeur === undefined ? '' : valeur)
        .toLowerCase().includes(cherche);
    }));
  }, [donnees, colonnes, terme]);

  const visibles = pagination
    ? filtrees.slice(page * parPage, page * parPage + parPage)
    : filtrees;

  const basculerSelection = (ligne) => {
    const suivantes = selectionnees.includes(ligne)
      ? selectionnees.filter((autre) => autre !== ligne)
      : [...selectionnees, ligne];
    setSelectionnees(suivantes);
    if (onSelectionChange) {
      onSelectionChange(suivantes);
    }
  };

  return (
    <Paper>
      {(titre || recherche) && (
        <Toolbar className={classes.barre}>
          {titre && <Typography variant="h6" component="h3">{titre}</Typography>}
          {recherche && (
            <TextField
              // Souligné plutôt qu'encadré : le cadre dessinait une seconde
              // boîte à l'intérieur de la carte du tableau, deux bordures à
              // deux centimètres l'une de l'autre pour un seul champ.
              variant="standard"
              className={classes.recherche}
              value={terme}
              onChange={(evenement) => {
                setTerme(evenement.target.value);
                // Revenir en première page : chercher puis rester page 3 donne
                // un tableau vide, et on croit que la recherche n'a rien trouvé.
                setPage(0);
              }}
              placeholder={t('table.search')}
              InputProps={{
                // ⚠️ « inputProps » minuscule vise le <input> lui-même, et
                // « InputProps » le composant qui l'enveloppe : MUI distingue
                // les deux par la casse, mais ESLint les voit en double. On
                // passe donc l'étiquette par le second, qui la transmet.
                'aria-label': t('table.search'),
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
              }}
            />
          )}
        </Toolbar>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {selection && <TableCell padding="checkbox" />}
              {colonnes.map((colonne) => (
                <TableCell key={colonne.field}>{colonne.title}</TableCell>
              ))}
              {!!actions.length && (
                <TableCell className={classes.actions}>{t('table.actions')}</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibles.map((ligne, index) => (
              <TableRow
                // Les données de l'API portent un `id` ; le repli sur l'indice
                // ne sert qu'aux lignes qui n'en auraient pas.
                key={ligne.id === undefined ? index : ligne.id}
                hover={!!onRowClick}
                className={onRowClick ? classes.ligneCliquable : undefined}
                style={styleDeLigne ? styleDeLigne(ligne) : undefined}
                onClick={onRowClick ? (evenement) => onRowClick(evenement, ligne) : undefined}
              >
                {selection && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectionnees.includes(ligne)}
                      onChange={() => basculerSelection(ligne)}
                      inputProps={{ 'aria-label': t('table.select_row') }}
                    />
                  </TableCell>
                )}
                {colonnes.map((colonne) => (
                  <TableCell key={colonne.field}>
                    {afficher(valeurDe(ligne, colonne.field), colonne)}
                  </TableCell>
                ))}
                {!!actions.length && (
                  <TableCell className={classes.actions}>
                    {actions.map((action) => (
                      <Tooltip key={action.tooltip} title={action.tooltip}>
                        <IconButton
                          size="small"
                          aria-label={action.tooltip}
                          onClick={(evenement) => {
                            // Sans ça, cliquer sur « retirer » déclencherait
                            // AUSSI l'ouverture de la ligne.
                            evenement.stopPropagation();
                            action.onClick(evenement, ligne);
                          }}
                        >
                          {action.icon()}
                        </IconButton>
                      </Tooltip>
                    ))}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && (
        <TablePagination
          component="div"
          count={filtrees.length}
          page={page}
          onPageChange={(evenement, suivante) => setPage(suivante)}
          rowsPerPage={parPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          onRowsPerPageChange={(evenement) => {
            setParPage(parseInt(evenement.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage={t('table.rows')}
          // ⚠️ Sans ça, MUI affiche « 1-3 of 3 » en anglais au milieu d'une
          // interface française. C'était traduit avant, et personne ne
          // remarque une régression de ce genre dans un diff.
          labelDisplayedRows={({ from, to, count }) => t('table.row_count', { from, to, count })}
        />
      )}
    </Paper>
  );
}

Tableau.propTypes = {
  colonnes: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    field: PropTypes.string.isRequired,
    type: PropTypes.string,
  })).isRequired,
  donnees: PropTypes.arrayOf(PropTypes.object),
  titre: PropTypes.string,
  actions: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.func.isRequired,
    tooltip: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  })),
  onRowClick: PropTypes.func,
  styleDeLigne: PropTypes.func,
  recherche: PropTypes.bool,
  pagination: PropTypes.bool,
  selection: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  taillePage: PropTypes.number,
};
